// Where each figure a contractor read is printed, and whether it really is
// (CINV-30).
//
// The model quotes the text that shows each value and names its page (rule 13);
// it is never asked for a position, because models read pages well and place
// boxes badly. The worker then checks every quote against the PDF's own text
// layer — the text itself is never stored, it holds bank details (CINV-14) —
// and keeps only the outcome, `InvoiceGrounding`, which the engine reads. One
// normaliser (`searchKey`) for the worker and the browser, so a quote the
// server found is a quote the page can highlight.

import type { CheckResult } from "./constants.js";
import { check, type InvoiceCheck } from "./invoice-check.js";
import {
  SOURCED_FIELDS,
  type ContractorInvoiceExtraction,
  type SourcedField,
} from "./invoice-extraction.js";
import { nameKey } from "./invoice-matching.js";
import {
  invoiceNumberKey,
  type CorrectableField,
  type NormalisedInvoice,
} from "./invoice-normalise.js";

/** Text as it is compared: NFKC, lower case, one kind of dash and quote, no whitespace at all. */
export function searchKey(text: string): string {
  return text
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[‐‑‒–—―−]/g, "-")
    .replace(/[‘’‚‛]/g, "'")
    .replace(/[“”„‟]/g, '"')
    .replace(/\s+/g, "");
}

/**
 * GROUNDED: the quote is on the page, and the value is in the quote.
 * VALUE_ON_PAGE: the quote is not on the page as given, but the value itself is.
 * NOT_ON_PAGE: neither — the figure may not be on the document at all.
 * QUOTE_ONLY: a scan, with no text to check; the value is in its own quote.
 * VALUE_NOT_IN_QUOTE: a scan whose quote does not even show the value read.
 * NO_QUOTE: a scan, and the model quoted nothing for a value it read.
 */
export type GroundStatus =
  | "GROUNDED"
  | "VALUE_ON_PAGE"
  | "NOT_ON_PAGE"
  | "QUOTE_ONLY"
  | "VALUE_NOT_IN_QUOTE"
  | "NO_QUOTE";

export type GroundedSource = { status: GroundStatus; page: number | null };

export type InvoiceGrounding = {
  v: 1;
  /** What the PDF's text layer gave: text, nothing (a scan), or nothing usable. */
  text: "TEXT" | "NO_TEXT" | "FAILED";
  /** Only the fields with a value; a null field has nothing to find. */
  fields: Partial<Record<SourcedField, GroundedSource>>;
  /** One per line item, in order. */
  lines: GroundedSource[];
};

const MONTHS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

/** Every way a money amount is printed, as search keys: 6,490.00 · 6490.00 · 6,490 · 6490. */
function moneyTokens(n: number): string[] {
  const abs = Math.abs(n);
  const fixed = abs.toFixed(2);
  const grouped = abs.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const indian = abs.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const tokens = [fixed, grouped, indian];
  if (Number.isInteger(abs)) tokens.push(String(abs), grouped.replace(/\.00$/, ""), indian.replace(/\.00$/, ""));
  return [...new Set(tokens.map(searchKey))];
}

/** Every way a day is printed: 30/09/2026, 30-09-26, 30 Sep 2026, Sep 30, 2026, 2026-09-30. */
function dateTokens(ymd: string): string[] {
  const [y, m, d] = ymd.split("-").map(Number) as [number, number, number];
  const yy = String(y).slice(2);
  const days = [String(d), String(d).padStart(2, "0")];
  const months = [String(m), String(m).padStart(2, "0")];
  const names = [MONTHS[m - 1]!, MONTHS[m - 1]!.slice(0, 3)];
  const out: string[] = [ymd];
  for (const dd of days) {
    for (const sep of ["/", "-", "."])
      for (const mm of months) for (const year of [String(y), yy]) out.push(`${dd}${sep}${mm}${sep}${year}`);
    for (const name of names)
      for (const year of [String(y), yy]) {
        out.push(`${dd}${name}${year}`, `${dd}-${name}-${year}`, `${name}${dd},${year}`, `${dd}${name},${year}`);
        out.push(`${dd}th${name}${year}`, `${dd}st${name}${year}`, `${dd}nd${name}${year}`, `${dd}rd${name}${year}`);
      }
  }
  return [...new Set(out.map(searchKey))];
}

/** A month printed as a month: September 2026 · Sep-26 · Sep 2026 · 09/2026. */
function monthTokens(ymd: string): string[] {
  const [y, m] = ymd.split("-").map(Number) as [number, number];
  const yy = String(y).slice(2);
  const name = MONTHS[m - 1]!;
  const out = [`${name}${y}`, `${name.slice(0, 3)}${y}`, `${name.slice(0, 3)}-${yy}`, `${name}-${yy}`, `${String(m).padStart(2, "0")}/${y}`];
  return out.map(searchKey);
}

const lastDayOf = (ymd: string) => {
  const [y, m] = ymd.split("-").map(Number) as [number, number];
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
};

/** Does this text show this value? One answer for the quote and for the page. */
function shows(field: SourcedField, value: FieldValue, text: string): boolean {
  const key = searchKey(text);
  const any = (tokens: string[]) => tokens.some((t) => t.length > 0 && key.includes(t));
  switch (field) {
    case "subtotal":
    case "taxAmount":
    case "total":
      return typeof value === "number" && any(moneyTokens(value));
    case "invoiceDate":
      return typeof value === "string" && any(dateTokens(value));
    case "period": {
      if (!value || typeof value !== "object") return false;
      const { start, end } = value;
      // A whole month is printed as the month: "September 2026".
      const wholeMonth =
        start.endsWith("-01") && end.slice(0, 7) === start.slice(0, 7) && Number(end.slice(8)) === lastDayOf(start);
      if (wholeMonth && any(monthTokens(start))) return true;
      return any(dateTokens(start)) && any(dateTokens(end));
    }
    case "invoiceNumber":
      return typeof value === "string" && invoiceNumberKey(text).includes(invoiceNumberKey(value));
    case "supplierTaxId":
      return typeof value === "string" && text.replace(/[^0-9A-Za-z]/g, "").toUpperCase().includes(value.toUpperCase());
    case "supplierName": {
      if (typeof value !== "string") return false;
      const a = nameKey(value);
      const b = nameKey(text);
      return a.length > 0 && (b.includes(a) || (b.length >= 4 && a.includes(b)));
    }
    case "currency":
      return value === "AUD"
        ? /\$|\baud\b/i.test(text)
        : value === "INR"
          ? /₹|\brs\.?|\binr\b/i.test(text)
          : false;
  }
}

type FieldValue = string | number | { start: string; end: string } | null;

/** The value each sourced field holds on a read, as the normaliser left it. */
function valueOf(field: SourcedField, invoice: NormalisedInvoice): FieldValue {
  switch (field) {
    case "invoiceNumber":
      return invoice.invoiceNumber;
    case "invoiceDate":
      return invoice.invoiceDate;
    case "period":
      return invoice.periodStart && invoice.periodEnd ? { start: invoice.periodStart, end: invoice.periodEnd } : null;
    case "supplierName":
      return invoice.supplierName;
    case "supplierTaxId":
      return invoice.supplierAbn ?? invoice.supplierGstin;
    case "currency":
      return invoice.currency;
    case "subtotal":
      return invoice.subtotal;
    case "taxAmount":
      return invoice.taxAmount;
    case "total":
      return invoice.total;
  }
}

/** The page holding this text, the hinted page first; null when none does. */
export function findOnPages(pages: readonly string[], test: (page: string) => boolean, hint: number | null): number | null {
  if (hint !== null && hint >= 1 && hint <= pages.length && test(pages[hint - 1]!)) return hint;
  const i = pages.findIndex(test);
  return i === -1 ? null : i + 1;
}

/**
 * Ground one read against its PDF's text. The reply may predate sources (a
 * test fixture, a stored reply) — then nothing was quoted, and every value is
 * looked for on the page alone.
 */
export function groundRead(
  reply: Pick<ContractorInvoiceExtraction, "lineItems"> & Partial<Pick<ContractorInvoiceExtraction, "sources">>,
  normalised: NormalisedInvoice,
  text: { status: "TEXT" | "NO_TEXT" | "FAILED"; pages: readonly string[] }
): InvoiceGrounding {
  const hasText = text.status === "TEXT";
  const fields: InvoiceGrounding["fields"] = {};
  for (const field of SOURCED_FIELDS) {
    const value = valueOf(field, normalised);
    if (value === null) continue;
    const source = reply.sources?.[field];
    const quote = source?.quote ?? null;
    const hint = typeof source?.page === "number" ? Math.round(source.page) : null;
    const inQuote = quote !== null && shows(field, value, quote);
    if (hasText) {
      const quotedPage = inQuote ? findOnPages(text.pages, (p) => searchKey(p).includes(searchKey(quote)), hint) : null;
      if (quotedPage !== null) fields[field] = { status: "GROUNDED", page: quotedPage };
      else {
        const page = findOnPages(text.pages, (p) => shows(field, value, p), hint);
        fields[field] = page !== null ? { status: "VALUE_ON_PAGE", page } : { status: "NOT_ON_PAGE", page: null };
      }
    } else {
      fields[field] = {
        status: quote === null ? "NO_QUOTE" : inQuote ? "QUOTE_ONLY" : "VALUE_NOT_IN_QUOTE",
        page: hint,
      };
    }
  }
  const lines: GroundedSource[] = reply.lineItems.map((line) => {
    const quote = "quote" in line ? (line.quote ?? null) : null;
    const hint = "page" in line && typeof line.page === "number" ? Math.round(line.page) : null;
    if (quote === null) return { status: hasText ? "NOT_ON_PAGE" : "NO_QUOTE", page: null };
    if (!hasText) return { status: "QUOTE_ONLY", page: hint };
    const page = findOnPages(text.pages, (p) => searchKey(p).includes(searchKey(quote)), hint);
    return page !== null ? { status: "GROUNDED", page } : { status: "NOT_ON_PAGE", page: null };
  });
  return { v: 1, text: text.status, fields, lines };
}

/** The figures whose absence from the page is worth a person's look. */
const KEY_FIELDS: readonly SourcedField[] = ["total", "subtotal", "taxAmount", "invoiceNumber", "invoiceDate", "period"];

export const SOURCED_FIELD_LABEL: Record<SourcedField, string> = {
  invoiceNumber: "Invoice number",
  invoiceDate: "Invoice date",
  period: "Period",
  supplierName: "Supplier",
  supplierTaxId: "ABN / GSTIN",
  currency: "Currency",
  subtotal: "Subtotal",
  taxAmount: "Tax",
  total: "Total",
};

/** The correctable fields a sourced field stands for — a corrected one is not the model's any more. */
export const SOURCED_FIELD_COLUMNS: Record<SourcedField, readonly CorrectableField[]> = {
  invoiceNumber: ["invoiceNumber"],
  invoiceDate: ["invoiceDate"],
  period: ["periodStart", "periodEnd"],
  supplierName: ["supplierName"],
  supplierTaxId: ["supplierAbn", "supplierGstin"],
  currency: ["currency"],
  subtotal: ["subtotal"],
  taxAmount: ["taxAmount"],
  total: ["total"],
};

const FAILING: readonly GroundStatus[] = ["NOT_ON_PAGE", "VALUE_NOT_IN_QUOTE"];

/**
 * CINV-30's check. A key figure that is not on the PDF is a review — the model
 * may have read it from the wrong place, or made it up; any other figure is
 * information. A scan has no text to look in, so only its quotes can be held
 * to the values, and agreeing with them is information, not proof. A figure an
 * admin corrected is theirs, and is not traced.
 */
export function groundingCheck(
  grounding: InvoiceGrounding,
  correctedFields: readonly CorrectableField[]
): InvoiceCheck {
  const traced = SOURCED_FIELDS.filter(
    (f) => grounding.fields[f] && !SOURCED_FIELD_COLUMNS[f].some((c) => correctedFields.includes(c))
  );
  const failing = traced.filter((f) => FAILING.includes(grounding.fields[f]!.status));
  const keyFailing = failing.filter((f) => KEY_FIELDS.includes(f));
  const names = (fs: SourcedField[]) => fs.map((f) => SOURCED_FIELD_LABEL[f].toLowerCase()).join(", ");
  const firstField = (fs: SourcedField[]) => (fs[0] ? (SOURCED_FIELD_COLUMNS[fs[0]][0] ?? null) : null);

  if (grounding.text === "FAILED")
    return check("GROUNDING", "On the PDF", "INFO", "The PDF's text could not be read, so the figures were not traced to it.");
  if (grounding.text === "NO_TEXT") {
    if (keyFailing.length > 0)
      return check(
        "GROUNDING",
        "On the PDF",
        "REVIEW",
        `A scan: the ${names(keyFailing)} read is not in the text quoted for it. Check against the page.`,
        { field: firstField(keyFailing) }
      );
    return check("GROUNDING", "On the PDF", "INFO", "A scan: the figures were checked against what was quoted for them only.");
  }
  if (traced.length === 0) return check("GROUNDING", "On the PDF", "INFO", "Nothing read to trace.");
  const result: CheckResult = keyFailing.length > 0 ? "REVIEW" : failing.length > 0 ? "INFO" : "MATCH";
  return check(
    "GROUNDING",
    "On the PDF",
    result,
    failing.length === 0
      ? "Every figure read was found on the PDF."
      : `Not found on the PDF: ${names(failing)}. ${keyFailing.length > 0 ? "Check them against the page." : ""}`.trim(),
    { field: firstField(keyFailing.length > 0 ? keyFailing : failing) }
  );
}

/** A stored grounding, or null when it is not one this code wrote. */
export function parseGrounding(json: string | null): InvoiceGrounding | null {
  if (!json) return null;
  try {
    const value = JSON.parse(json) as InvoiceGrounding;
    return value && value.v === 1 && typeof value.fields === "object" && Array.isArray(value.lines) ? value : null;
  } catch {
    return null;
  }
}

// --- Keeping bank details out of the quotes (CINV-14) --------------------------

const BANK_WORDS = /\b(bsb|account|acct|a\/c|ifsc|iban|swift|bic|upi|sort\s*code|routing)\b/i;
const IFSC_CODE = /\b[A-Z]{4}0[A-Z0-9]{6}\b/;
const IBAN_CODE = /\b[A-Z]{2}\d{2}[A-Z0-9]{11,30}\b/;
const UPI_ID = /\b[\w.-]{2,}@[a-z]{2,}\b/i;

/** A quote that could carry a bank detail. Over-cautious on purpose: losing a highlight costs nothing. */
export function looksLikeBankDetail(quote: string): boolean {
  return BANK_WORDS.test(quote) || IFSC_CODE.test(quote) || IBAN_CODE.test(quote) || UPI_ID.test(quote);
}

/**
 * The reply with every quote that could carry a bank detail taken out, and a
 * warning saying so. Rule 13 tells the model never to quote one; this is what
 * makes sure the stored reply cannot hold one either way. Nothing changes —
 * and the reply is stored verbatim (CINV-7) — unless a quote is taken out.
 */
export function scrubBankQuotes(reply: ContractorInvoiceExtraction): {
  reply: ContractorInvoiceExtraction;
  scrubbed: boolean;
} {
  let scrubbed = false;
  // Tolerant of a reply with no sources at all: nothing quoted, nothing to take out.
  const clean = <S extends { quote?: string | null; page?: number | null }>(source: S): S => {
    if (!source.quote || !looksLikeBankDetail(source.quote)) return source;
    scrubbed = true;
    return { ...source, quote: null, page: null };
  };
  const sources = Object.fromEntries(
    Object.entries(reply.sources ?? {}).map(([field, source]) => [field, clean(source)])
  ) as ContractorInvoiceExtraction["sources"];
  const lineItems = reply.lineItems.map((line) => clean(line));
  if (!scrubbed) return { reply, scrubbed };
  return {
    reply: {
      ...reply,
      sources,
      lineItems,
      warnings: [...reply.warnings, "A quote that could hold bank details was not kept."],
    },
    scrubbed,
  };
}

// --- What the review screen highlights ------------------------------------------

type FieldSourceView = { quotes: { text: string; page: number | null }[]; status: GroundStatus | null };

/**
 * Each correctable field's quotes, for "Show on PDF": the sourced field it
 * stands for, or — for the figures summed from lines (hours, days, rate, the
 * work and the other charges) — the lines they came from. The status is the
 * check's, so a figure not found on the PDF says so beside it.
 */
export function fieldSourcesOf(
  reply: Pick<ContractorInvoiceExtraction, "lineItems" | "sources">,
  grounding: InvoiceGrounding | null
): Partial<Record<CorrectableField, FieldSourceView>> {
  const out: Partial<Record<CorrectableField, FieldSourceView>> = {};
  for (const field of SOURCED_FIELDS) {
    const source = reply.sources[field];
    if (!source.quote) continue;
    const view: FieldSourceView = {
      quotes: [{ text: source.quote, page: grounding?.fields[field]?.page ?? source.page }],
      status: grounding?.fields[field]?.status ?? null,
    };
    for (const column of SOURCED_FIELD_COLUMNS[field]) out[column] = view;
  }
  const linesOf = (expense: boolean): FieldSourceView | null => {
    const quotes = reply.lineItems
      .map((line, i) => ({ line, ground: grounding?.lines[i] ?? null }))
      .filter(({ line }) => line.isExpense === expense && line.quote)
      .map(({ line, ground }) => ({ text: line.quote!, page: ground?.page ?? line.page }));
    return quotes.length > 0 ? { quotes, status: null } : null;
  };
  const labour = linesOf(false);
  if (labour) for (const column of ["hoursClaimed", "daysClaimed", "unitRate", "rateUnit", "labourAmount"] as const) out[column] = labour;
  const extras = linesOf(true);
  if (extras) out.otherCharges = extras;
  return out;
}
